/* BankingRoleDetail.jsx — one preview per banking role.
   Reads /data/banking_roles.json (Banking Roles Master · 120 roles · with quizzes
   + AI Agent Integration). UI matches /previews + /pathways/switch + /explore/ai
   pattern: cream bg, big teal gradient hero, sticky pill nav, bordered cards. */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Quote, Briefcase, BookOpen, Map, Wrench, Lightbulb,
  ShieldAlert, MessageCircle, Sparkles, Brain, Bot, ChevronLeft, ChevronRight,
  GraduationCap, Activity,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import ContentLockedCard from '../components/ContentLockedCard';
import PreviewUnlockOverlay from '../components/PreviewUnlockOverlay';
import { usePreviewQuota } from '../hooks/usePreviewQuota';
import { loadContent, isLocked } from '../lib/contentLoader';
import {
  Section, InfoCard, Bullets, ChipList, PillNav, QuizYesNoMcq,
} from '../components/preview-ui/PreviewPrimitives';

const SECTIONS = [
  { id: 'eligibility',   label: 'Eligibility',           icon: GraduationCap },
  { id: 'daily-work',    label: 'Daily Work',            icon: Briefcase },
  { id: 'transfers',     label: 'Transfers & Pressure',  icon: Map },
  { id: 'skills',        label: 'Skills',                icon: Wrench },
  { id: 'tools',         label: 'Tools',                 icon: BookOpen },
  { id: 'projects',      label: 'Student Projects',      icon: Lightbulb },
  { id: 'ai-risk',       label: 'AI Risk',               icon: ShieldAlert },
  { id: 'ai-agents',     label: 'AI Agent Integration',  icon: Bot },
  { id: 'interview',     label: 'Interview Answer',      icon: MessageCircle },
  { id: 'readiness',     label: 'Quick Readiness',       icon: Activity },
  { id: 'quiz',          label: 'Quiz',                  icon: Brain },
];

const BankingRoleDetail = () => {
  const { catalogNumber } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  usePreviewQuota(`banking-${catalogNumber}`, 'banking');

  useEffect(() => {
    loadContent('banking_roles.json')
      .then((d) => {
        if (isLocked(d)) { setLocked(true); setLoading(false); return; }
        if (d) setPkg(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [catalogNumber]);

  const { role, adjacent } = useMemo(() => {
    if (!pkg?.roles) return { role: null, adjacent: { prev: null, next: null } };
    const sorted = [...pkg.roles].sort((a, b) => (a.catalog_number || 0) - (b.catalog_number || 0));
    const idx = sorted.findIndex((r) => String(r.catalog_number) === String(catalogNumber));
    return {
      role: idx >= 0 ? sorted[idx] : null,
      adjacent: {
        prev: idx > 0 ? sorted[idx - 1] : null,
        next: idx >= 0 && idx < sorted.length - 1 ? sorted[idx + 1] : null,
      },
    };
  }, [pkg, catalogNumber]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <div className="flex items-center justify-center min-h-[70vh]" data-testid="banking-loading">
          <Loader2 className="w-10 h-10 text-teal-600 animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (locked) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <div className="pt-32 px-4"><ContentLockedCard /></div>
        <Footer />
      </div>
    );
  }

  if (!role) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md" data-testid="banking-not-found">
            <p className="text-2xl font-bold text-gray-900 mb-2">Role not found</p>
            <p className="text-gray-600 mb-6">We couldn&apos;t find banking role #{catalogNumber}.</p>
            <Button asChild>
              <Link to="/explore/careers-ts-ap">Back to Careers</Link>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const elig = role.verified_eligibility_reality_check || {};
  const ai = role.ai_agent_integration || {};
  const risk = role.ai_risk_and_staying_relevant || {};

  return (
    <PreviewUnlockOverlay
      pathKey="banking_bluecollar"
      previewId={String(role.catalog_number)}
      itemName={role.role_name}
      itemType="banking"
    >
    <div className="min-h-screen bg-[#FFF9F1]">
      <Navbar />

      {/* Hero — path-specific deep emerald → forest */}
      <section className="relative pt-32 md:pt-36 pb-12 px-4 md:px-6 bg-gradient-to-br from-emerald-600 via-green-700 to-teal-800 text-white rounded-b-[2.5rem] overflow-hidden">
        <div className="absolute -top-12 right-8 w-48 h-48 rounded-full bg-white/10" aria-hidden="true" />
        <div className="absolute bottom-0 -left-10 w-36 h-36 rounded-full bg-white/10" aria-hidden="true" />
        <div className="max-w-4xl mx-auto relative">
          <Link
            to="/explore/careers-ts-ap"
            data-testid="banking-back"
            className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-5 transition-colors font-semibold"
          >
            <ArrowLeft size={14} /> Back to Careers
          </Link>
          <div className="flex flex-wrap items-center gap-1.5 mb-5 text-[10px] font-semibold tracking-wide uppercase">
            <span className="bg-white/20 text-white/95 px-2.5 py-0.5 rounded-full tabular-nums">
              #{String(role.catalog_number).padStart(3, '0')}
            </span>
            {(() => {
              const seen = new Set();
              const chips = [role.role_family, 'Banking · AI-Agent-Integrated']
                .filter(Boolean)
                .filter((c) => {
                  const k = String(c).trim().toLowerCase();
                  if (!k || seen.has(k)) return false;
                  seen.add(k);
                  return true;
                });
              return chips.map((c, i) => (
                <span key={i} className="bg-white/15 text-white/90 px-2.5 py-0.5 rounded-full">{c}</span>
              ));
            })()}
          </div>
          <h1
            data-testid="banking-title"
            className="text-3xl md:text-5xl font-bold leading-[1.15] tracking-tight mb-7"
          >
            {role.role_name}
          </h1>
          {role.daily_work_preview && (
            <blockquote className="relative bg-white/10 backdrop-blur border border-white/20 rounded-3xl px-6 py-6 md:px-8 md:py-8">
              <Quote size={26} className="text-amber-300 mb-3" />
              <p className="text-lg md:text-2xl text-white leading-[1.5] italic">{role.daily_work_preview}</p>
            </blockquote>
          )}
        </div>
      </section>

      <PillNav sections={SECTIONS} testidPrefix="banking-nav" hoverTone="emerald" />

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 space-y-6">

        {/* Eligibility */}
        {Object.keys(elig).length > 0 && (
          <Section id="eligibility" icon={GraduationCap} title="Eligibility — Reality Check" accent="emerald">
            <div className="grid md:grid-cols-2 gap-3">
              {elig.age && <InfoCard label="Age" tone="mint">{elig.age}</InfoCard>}
              {elig.education_branch_rule && <InfoCard label="Education & branch rule" tone="sky">{elig.education_branch_rule}</InfoCard>}
              {elig.entry_route && <InfoCard label="Entry route" tone="violet">{elig.entry_route}</InfoCard>}
              {elig.student_warning && <InfoCard label="Warning" tone="rose">{elig.student_warning}</InfoCard>}
            </div>
          </Section>
        )}

        {/* Daily work */}
        {role.daily_work_preview && (
          <Section id="daily-work" icon={Briefcase} title="Daily Work Preview" accent="banking_primary">
            <p>{role.daily_work_preview}</p>
          </Section>
        )}

        {/* Transfers & pressure */}
        {role.transfers_location_work_pressure && (
          <Section id="transfers" icon={Map} title="Transfers, Location & Work Pressure" accent="amber">
            <p>{role.transfers_location_work_pressure}</p>
          </Section>
        )}

        {/* Skills */}
        {role.skills_students_should_build?.length > 0 && (
          <Section id="skills" icon={Wrench} title="Skills Students Should Build" accent="banking_primary">
            <Bullets items={role.skills_students_should_build} tone="teal" />
          </Section>
        )}

        {/* Tools */}
        {role.tools_to_learn_or_understand?.length > 0 && (
          <Section id="tools" icon={BookOpen} title="Tools to Learn or Understand" accent="banking_primary">
            <ChipList items={role.tools_to_learn_or_understand} tone="blue" />
          </Section>
        )}

        {/* Useful projects */}
        {role.useful_student_projects?.length > 0 && (
          <Section id="projects" icon={Lightbulb} title="Useful Student Projects" accent="banking_primary">
            <div className="grid sm:grid-cols-2 gap-3">
              {role.useful_student_projects.map((p, i) => (
                <div key={i} className="rounded-2xl bg-amber-50 border border-amber-200 p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-amber-700 font-extrabold text-base shrink-0">{String(i + 1).padStart(2, '0')}</span>
                    <p className="text-[15px] text-gray-800 leading-relaxed">{p}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* AI Risk */}
        {(risk.ai_risk_summary || risk.stay_relevant) && (
          <Section id="ai-risk" icon={ShieldAlert} title="AI Risk & Staying Relevant" accent="rose">
            <div className="grid md:grid-cols-2 gap-3">
              {risk.ai_risk_summary && <InfoCard label="AI risk summary" tone="rose">{risk.ai_risk_summary}</InfoCard>}
              {risk.stay_relevant && <InfoCard label="How to stay relevant" tone="mint">{risk.stay_relevant}</InfoCard>}
            </div>
          </Section>
        )}

        {/* AI Agent Integration */}
        {Object.keys(ai).length > 0 && (
          <Section id="ai-agents" icon={Bot} title="AI Agent Integration" accent="banking_primary">
            <div className="space-y-3">
              {ai.role_specific_agent_use && <InfoCard label="Role-specific agent use" tone="violet">{ai.role_specific_agent_use}</InfoCard>}
              {ai.role_specific_human_edge && <InfoCard label="Role-specific human edge" tone="mint">{ai.role_specific_human_edge}</InfoCard>}
              {ai.how_agents_are_used_in_this_role && <InfoCard label="How agents are used in this role" tone="sky">{ai.how_agents_are_used_in_this_role}</InfoCard>}
              {ai.what_stays_human_in_this_role && <InfoCard label="What stays human in this role" tone="cream">{ai.what_stays_human_in_this_role}</InfoCard>}
              {ai.how_to_work_with_agents && <InfoCard label="How to work with agents" tone="violet">{ai.how_to_work_with_agents}</InfoCard>}
              {ai.agent_human_loop && <InfoCard label="Agent ↔ human loop" tone="cream"><span className="font-medium">{ai.agent_human_loop}</span></InfoCard>}
              {ai.agent_ready_action_for_students && <InfoCard label="Agent-ready action for students" tone="sky">{ai.agent_ready_action_for_students}</InfoCard>}
            </div>
          </Section>
        )}

        {/* Interview-ready */}
        {role.interview_ready_explanation && (
          <Section id="interview" icon={MessageCircle} title="Interview-Ready Explanation" accent="banking_primary">
            <div className="rounded-2xl bg-violet-50 border border-violet-200 p-4 md:p-5">
              <div className="flex items-start gap-3">
                <Quote size={20} className="text-violet-600 shrink-0 mt-1" />
                <p className="text-[15px] text-gray-800 leading-relaxed italic">{role.interview_ready_explanation}</p>
              </div>
            </div>
          </Section>
        )}

        {/* Quick readiness test */}
        {role.quick_readiness_test?.length > 0 && (
          <Section id="readiness" icon={Activity} title="Quick Readiness Test" accent="banking_primary">
            <Bullets items={role.quick_readiness_test} tone="teal" />
          </Section>
        )}

        {/* Quiz */}
        {role.role_quiz && (
          <Section id="quiz" icon={Brain} title="Role Quiz — Try It" accent="amber">
            <QuizYesNoMcq
              quiz={role.role_quiz}
              scoringGuide={role.role_quiz.scoring_guide}
              testidPrefix="banking-quiz"
            />
          </Section>
        )}

        {/* Prev / Next */}
        <nav className="mt-6 grid sm:grid-cols-2 gap-3" aria-label="Adjacent banking roles">
          {adjacent.prev ? (
            <button
              type="button"
              data-testid="banking-prev"
              onClick={() => navigate(`/explore/careers-ts-ap/banking/${adjacent.prev.catalog_number}`)}
              className="text-left p-4 md:p-5 rounded-2xl border-2 border-gray-200 bg-white hover:border-emerald-500 hover:shadow-md transition-all group"
            >
              <p className="text-[11px] font-bold tracking-widest text-gray-500 uppercase mb-1 flex items-center gap-1">
                <ChevronLeft size={14} /> Previous
              </p>
              <p className="font-bold text-gray-900 truncate group-hover:text-emerald-700">{adjacent.prev.role_name}</p>
            </button>
          ) : <span />}
          {adjacent.next ? (
            <button
              type="button"
              data-testid="banking-next"
              onClick={() => navigate(`/explore/careers-ts-ap/banking/${adjacent.next.catalog_number}`)}
              className="text-right p-4 md:p-5 rounded-2xl border-2 border-gray-200 bg-white hover:border-emerald-500 hover:shadow-md transition-all group sm:col-start-2"
            >
              <p className="text-[11px] font-bold tracking-widest text-gray-500 uppercase mb-1 flex items-center justify-end gap-1">
                Next <ChevronRight size={14} />
              </p>
              <p className="font-bold text-gray-900 truncate group-hover:text-emerald-700">{adjacent.next.role_name}</p>
            </button>
          ) : null}
        </nav>

      </main>

      <Footer />
    </div>
    </PreviewUnlockOverlay>
  );
};

export default BankingRoleDetail;
